import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoCostTracker() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">Cost Tracking</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Cost Tracker: Master Your Shopping Budget</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">Learn how to track every dollar with a detailed BaseTao cost tracking system that gives you complete financial visibility.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Why Cost Tracking Matters</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">When shopping through BaseTao, costs can add up quickly. Product prices, shipping fees, warehouse charges, and currency conversion fees all contribute to your final spend. A <strong className="text-foreground-900">BaseTao Cost Tracker</strong> spreadsheet gives you a clear, real-time picture of where every dollar is going.</p>
              <p className="text-foreground-600 leading-relaxed">Without proper cost tracking, it is easy to lose sight of your total spending — especially when you have multiple items in different warehouse stages. The cost tracker solves this by consolidating all expenses into one organized view.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Essential Cost Categories to Track</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[{label:'Product Price',desc:'The listed price of each item before any additional fees'},{label:'Domestic Shipping',desc:'Shipping from the seller to the BaseTao warehouse within China'},{label:'International Shipping',desc:'Shipping from the BaseTao warehouse to your destination country'},{label:'Service Fees',desc:'Any platform or agent service charges applied to your order'},{label:'Currency Conversion',desc:'Exchange rate differences between your currency and the listed price'},{label:'Customs & Duties',desc:'Potential import taxes or customs fees for your country'}].map((item,i)=>(
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center mb-2"><i className="ri-price-tag-3-line text-sm text-primary-600"></i></div>
                    <h3 className="font-heading text-sm text-foreground-900 mb-1">{item.label}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Setting Up Automated Calculations</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">The real power of a BaseTao Cost Tracker comes from automated formulas. Set up your spreadsheet so that when you enter a product price and shipping estimate, the final cost column calculates automatically.</p>
              <p className="text-foreground-600 leading-relaxed">Add a summary row at the bottom showing total product cost, total shipping cost, and grand total. Use conditional formatting to flag items that exceed your budget threshold. For more spreadsheet techniques, see our <Link to="/basetao-spreadsheet-tutorial/" className="text-primary-600 hover:text-primary-700 font-medium">spreadsheet tutorial</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Start Tracking Your Costs</h3>
              <p className="text-foreground-600 text-sm mb-4">Visit BaseTao Spreadsheet to explore products and begin your cost tracking journey.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors duration-200 whitespace-nowrap">Explore Products <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">Spreadsheet Guide</Link>
              <Link to="/basetao-shipping-calculator/" className="hover:text-primary-600 transition-colors">Shipping Calculator</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Cost Tracker: Master Your Shopping Budget',
            description: 'Learn how to track every dollar with a detailed BaseTao cost tracking system that gives you complete financial visibility.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-cost-tracker/',
            },
          }),
        }}
      />
    </div>
  );
}